// ─────────────────────────────────────────────────────────────
//  /teampayout – ungelisteter Payout-Rechner.
//  Logik aus provisions_rechner.html, Marken-Sprache 1:1 vom
//  Team-Dashboard / Provisionsrechner (Karten, Regler, Share-Card).
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { easeIOS, staggerContainer, itemVariants } from './dashboardMotion';
import {
  APPT_MAX,
  APPT_MIN,
  buildShareQuery,
  calcTeamProvi,
  parseInput,
  readShareFromLocation,
  teamProviBasePath,
} from './teamProviCore';
import './TeamProvi.css';

const fmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fillPct = (v, lo, hi) => `${((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * 100}%`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const hideFromSearch = () => {
  const prevTitle = document.title;
  document.title = 'Payout Rechner';
  document.documentElement.classList.add('tp-lock');
  document.body.classList.add('tp-lock');

  const robots = document.querySelector('meta[name="robots"]');
  const prevRobots = robots ? robots.getAttribute('content') : null;
  if (robots) {
    robots.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
  } else {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(meta);
  }

  const canonical = document.querySelector('link[rel="canonical"]');
  const prevCanonical = canonical ? canonical.getAttribute('href') : null;
  if (canonical) canonical.remove();

  return () => {
    document.title = prevTitle;
    document.documentElement.classList.remove('tp-lock');
    document.body.classList.remove('tp-lock');
    const currentRobots = document.querySelector('meta[name="robots"]');
    if (currentRobots) {
      if (prevRobots != null) currentRobots.setAttribute('content', prevRobots);
      else if (!robots) currentRobots.remove();
    }
    if (canonical && prevCanonical != null) {
      document.head.appendChild(canonical);
      canonical.setAttribute('href', prevCanonical);
    }
  };
};

const Earner = ({ index, tone, name, role, amount, note, noteTone, rate, tag, extra }) => (
  <div className={`tp-earner tp-earner-${tone}`}>
    <div className="tp-earner-head">
      <span className={`tp-earner-badge tp-badge-${tone}`}>{index}</span>
      <span className="tp-earner-name">{name}</span>
      {role && <span className="tp-earner-role">{role}</span>}
      {extra}
    </div>
    <div className={`tp-earner-value${noteTone === 'neg' ? ' tp-earner-neg' : ''}`}>{amount}</div>
    {rate && (
      <div className="tp-earner-rate">
        <strong>{rate}</strong>
        <span>pro Termin</span>
      </div>
    )}
    {note && <div className={`tp-earner-break${noteTone ? ` tp-note-${noteTone}` : ''}`}>{note}</div>}
    {tag && <span className="tp-tag-min">{tag}</span>}
  </div>
);

const TeamProvi = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const shared = readShareFromLocation(location.pathname, location.search);

  const [priceText, setPriceText] = useState(shared?.price || '290');
  const [apptText, setApptText] = useState(shared?.appointments || '8');
  const [fixumOpen, setFixumOpen] = useState(parseInput(shared?.fixum) > 0 || parseInput(shared?.included) > 0);
  const [fixumText, setFixumText] = useState(shared?.fixum || '');
  const [includedText, setIncludedText] = useState(shared?.included || '');
  const [extraRole, setExtraRole] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(hideFromSearch, []);

  useEffect(() => {
    const incoming = readShareFromLocation(location.pathname, location.search);
    if (!incoming) return;
    if (incoming.price && parseInput(incoming.price) !== parseInput(priceText)) setPriceText(incoming.price);
    if (incoming.appointments && parseInput(incoming.appointments) !== parseInput(apptText)) {
      setApptText(incoming.appointments);
    }
    if (incoming.fixum && parseInput(incoming.fixum) !== parseInput(fixumText)) {
      setFixumText(incoming.fixum);
      setFixumOpen(true);
    }
    if (incoming.included && parseInput(incoming.included) !== parseInput(includedText)) {
      setIncludedText(incoming.included);
      setFixumOpen(true);
    }
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const price = parseInput(priceText);
  const appointments = parseInput(apptText) || 1;
  const fixum = fixumOpen ? parseInput(fixumText) : 0;
  const included = fixumOpen ? parseInput(includedText) : 0;
  const result = useMemo(
    () => calcTeamProvi({ base: price, appointments, extraRole, fixum, included }),
    [price, appointments, extraRole, fixum, included],
  );

  useEffect(() => {
    if (!(price > 0)) return;
    const path = teamProviBasePath(location.pathname);
    const target = `${path}${buildShareQuery(price, appointments, fixum, included)}`;
    if (`${location.pathname}${location.search}` !== target) {
      navigate(target, { replace: true });
    }
  }, [price, appointments, fixum, included, location.pathname, location.search, navigate]);

  const sliderAppt = clamp(appointments, APPT_MIN, APPT_MAX);

  const bonusSteps = [
    { pct: 2, at: result.tiers[0] },
    { pct: 4, at: result.tiers[1] },
    { pct: 6, at: result.tiers[2] },
    { pct: 8, at: result.tiers[3] },
  ];

  return (
    <div className="tp-shell">
      <nav className="tp-nav">
        <div className="tp-nav-inner">
          <div className="tp-brand">
            <img src="/img/sg_bonusclub_combo_white.png" alt="SalesGod · BonusClub" className="tp-brand-logo" />
          </div>
        </div>
      </nav>

      <div className="tp-wrap">
        <motion.div className="tp-inner" variants={staggerContainer} initial="initial" animate="animate">
          <motion.header className="tp-head" variants={itemVariants}>
            <h1>Payout Rechner</h1>
            <div className="tp-eyebrow">BonusClub · Vergütungsplan</div>
          </motion.header>

          <motion.section className="tp-controls" variants={itemVariants}>
            <div className="tp-control">
              <div className="tp-control-top">
                <span className="tp-control-label">Betrag / Termin</span>
                <span className="tp-control-value">{fmt.format(result.base)}</span>
              </div>
              <div className="tp-input-suffix">
                <span className="tp-currency">€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={priceText}
                  autoComplete="off"
                  aria-label="Betrag pro Termin"
                  onChange={(e) => setPriceText(e.target.value)}
                />
                <span className="tp-hint">pro Termin</span>
              </div>
            </div>

            <div className="tp-control">
              <div className="tp-control-top">
                <span className="tp-control-label">Anzahl Termine</span>
                <input
                  type="text"
                  className="tp-control-value-input"
                  inputMode="decimal"
                  value={apptText}
                  autoComplete="off"
                  aria-label="Anzahl Termine"
                  onChange={(e) => setApptText(e.target.value)}
                />
              </div>
              <input
                type="range"
                min={APPT_MIN}
                max={APPT_MAX}
                step={1}
                value={sliderAppt}
                style={{ '--tp-fill': fillPct(sliderAppt, APPT_MIN, APPT_MAX) }}
                onChange={(e) => setApptText(e.target.value)}
              />
              <div className="tp-range-minmax">
                <span>{APPT_MIN}</span>
                <span>{APPT_MAX}+</span>
              </div>
            </div>

            {fixumOpen && (
              <>
                <div className="tp-control">
                  <div className="tp-control-top">
                    <span className="tp-control-label">Fixum</span>
                    <span className="tp-control-value-row">
                      <span className="tp-control-value">{fmt.format(result.fixum)}</span>
                      <button
                        type="button"
                        className="tp-remove"
                        aria-label="Fixum entfernen"
                        onClick={() => {
                          setFixumOpen(false);
                          setFixumText('');
                          setIncludedText('');
                        }}
                      >
                        ×
                      </button>
                    </span>
                  </div>
                  <div className="tp-input-suffix">
                    <span className="tp-currency">€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={fixumText}
                      autoComplete="off"
                      aria-label="Fixum"
                      onChange={(e) => setFixumText(e.target.value)}
                    />
                    <span className="tp-hint">wird aufgeteilt</span>
                  </div>
                </div>

                <div className="tp-control">
                  <div className="tp-control-top">
                    <span className="tp-control-label">Inkludierte Termine</span>
                    <span className="tp-control-value">{result.included || 0}</span>
                  </div>
                  <div className="tp-input-suffix">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={includedText}
                      autoComplete="off"
                      aria-label="Inkludierte Termine"
                      onChange={(e) => setIncludedText(e.target.value)}
                    />
                    <span className="tp-hint">im Fixum</span>
                  </div>
                </div>
              </>
            )}
          </motion.section>

          {!fixumOpen && (
            <motion.div className="tp-fixum-add" variants={itemVariants}>
              <button type="button" className="tp-add tp-add-fixum" onClick={() => setFixumOpen(true)}>
                <span>+</span>Fixum
              </button>
            </motion.div>
          )}

          <motion.div className="tp-share-tag" variants={itemVariants}>Aufteilung</motion.div>

          <motion.section className="tp-card" variants={itemVariants}>
            <div className="tp-card-head">
              <div className="tp-card-title">BonusClub <span>Auszahlung</span></div>
              <div className="tp-card-meta">
                <b>{fmt.format(result.base)}</b> / Termin · <b>{result.appointments}</b>{' '}
                {result.appointments === 1 ? 'Termin' : 'Termine'}
                {result.fixum > 0 && (
                  <> · <b>{fmt.format(result.fixum)}</b> Fixum</>
                )}
                {result.included > 0 && (
                  <> · <b>{result.included}</b> inkl.</>
                )}
              </div>
            </div>

            <div className={`tp-fold${salesOpen ? ' tp-fold-open' : ''}`}>
              <button
                type="button"
                className="tp-fold-toggle"
                aria-expanded={salesOpen}
                onClick={() => setSalesOpen((v) => !v)}
              >
                <span className="tp-fold-title">Sales & Fixkosten</span>
                <span className="tp-fold-meta">{fmt.format(-(result.salesRep + result.salesInternal))}</span>
                <motion.svg
                  className="tp-fold-chevron"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  animate={{ rotate: salesOpen ? 180 : 0 }}
                  transition={{ duration: 0.46, ease: easeIOS }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </motion.svg>
              </button>
              <AnimatePresence initial={false}>
                {salesOpen && (
                  <motion.div
                    className="tp-fold-panel"
                    key="sales-fold"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.48, ease: easeIOS },
                      opacity: { duration: 0.36, ease: easeIOS },
                    }}
                  >
                    <motion.div
                      className="tp-earners"
                      initial="hidden"
                      animate="show"
                      exit="hidden"
                      variants={{
                        show: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
                        hidden: { transition: { staggerChildren: 0.04, staggerDirection: -1 } },
                      }}
                    >
                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 10 },
                          show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: easeIOS } },
                        }}
                      >
                        <Earner
                          index={1}
                          tone="sales"
                          name="Sales Rep"
                          role="20 %"
                          amount={fmt.format(result.salesRep)}
                          note="Kundengewinnung & Vertragsabschluss"
                        />
                      </motion.div>
                      <motion.div
                        variants={{
                          hidden: { opacity: 0, y: 10 },
                          show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: easeIOS } },
                        }}
                      >
                        <Earner
                          index={2}
                          tone="internal"
                          name="Interne Aufwände"
                          role="20 %"
                          amount={fmt.format(result.salesInternal)}
                          note="Plattform, Listen, Akquise, Marketing, KI Credits, Email Accounts & Hosting"
                        />
                      </motion.div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="tp-block-label">Rest nach Sales · 60 %</div>
            <div className="tp-earners">
              <Earner
                index={3}
                tone="company"
                name="SalesGod Internal"
                role={`${result.companyPctOfRest} %`}
                amount={fmt.format(result.companyFinal)}
                note={result.companyNegative ? 'Trägt das Risiko · Konto im Minus' : 'Firmen Account'}
                noteTone={result.companyNegative ? 'neg' : undefined}
              />
              <Earner
                index={4}
                tone="ops"
                name="Account Managers"
                role={`${result.opsPctOfRest} %`}
                amount={fmt.format(result.opsFinal)}
                note="Outreach Team"
              />
            </div>

            <div className="tp-block-label">Account Managers im Detail</div>
            <div className={`tp-earners tp-detail-earners${extraRole ? ' tp-earners-3' : ''}`}>
              <Earner
                index={5}
                tone="outreach"
                name="Outreach Manager"
                role={`${Math.round(result.outreachSplit * 100)} %`}
                amount={fmt.format(result.outreach)}
                note={result.outreachFixumActive ? 'Fixum aktiv · Sicherheitsnetz 200 €' : 'Daily Outreach Operations'}
                noteTone={result.outreachFixumActive ? 'outreach' : undefined}
                rate={fmt.format(result.outreachPerTermin)}
                tag={result.outreachFixumActive ? 'Minimum 200 €' : null}
              />
              <div className="tp-join" aria-hidden="true">+</div>
              <Earner
                index={6}
                tone="owner"
                name="Account Manager"
                role={`${Math.round(result.ownerSplit * 100)} %`}
                amount={fmt.format(result.owner)}
                note={result.ownerFixumActive ? 'Fixum aktiv · Sicherheitsnetz 100 €' : 'Strategy & Kundenkommunikation'}
                noteTone={result.ownerFixumActive ? 'owner' : undefined}
                rate={fmt.format(result.ownerPerTermin)}
                tag={result.ownerFixumActive ? 'Minimum 100 €' : null}
              />
              {extraRole && (
                <Earner
                  index={7}
                  tone="extra"
                  name="Cold-Caller"
                  role={`${result.extraPctDisplay} %`}
                  amount={fmt.format(result.extra)}
                  note="Cold-Calling & Terminvorbereitung"
                  extra={(
                    <button
                      type="button"
                      className="tp-remove"
                      aria-label="Cold-Caller entfernen"
                      onClick={() => setExtraRole(false)}
                    >
                      ×
                    </button>
                  )}
                />
              )}
            </div>
          </motion.section>

          <motion.div className="tp-extra" variants={itemVariants}>
            {!extraRole && (
              <button type="button" className="tp-add" onClick={() => setExtraRole(true)}>
                <span>+</span>Cold-Caller hinzufügen
              </button>
            )}
          </motion.div>

          <motion.section className={`tp-bonus-card${result.bonusPct > 0 ? ' tp-bonus-card-on' : ''}`} variants={itemVariants}>
            <div className="tp-bonus-head">
              <div className="tp-bonus-title">Bonus</div>
              <div className={`tp-bonus-status${result.bonusPct > 0 ? ' is-on' : ''}`}>
                {result.bonusLabel}
              </div>
            </div>
            <div className="tp-unlock-head">{result.priceClassLabel}</div>
            <div className="tp-unlock-list">
              {bonusSteps.map((step) => {
                const on = result.appointments >= step.at;
                return (
                  <div key={step.pct} className={`tp-unlock${on ? ' tp-unlock-on' : ''}`}>
                    <span>ab {step.at} Terminen</span>
                    <span className="tp-unlock-val">+{step.pct} %</span>
                  </div>
                );
              })}
            </div>
          </motion.section>
        </motion.div>
      </div>
    </div>
  );
};

export default TeamProvi;
